import React, { useState, useEffect } from "react";
import { Check, Briefcase, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import { backendApi } from "@/api/backendApi";
import { useAuth } from "../../context/AuthContext";

export default function Register() {
  const navigate = useNavigate();

  const { user, login } = useAuth();

  // Immediately redirect if already logged in
  useEffect(() => {
    if (user) navigate("/jobs/dashboard", { replace: true });
  }, [navigate, user]);

  // ----------- FORM STATE -------------
  const [form, setForm] = useState({
    fullname: "",
    email: "",
    password: "",
    mobile: "",
    work_status: "",
    updates: false,
  });

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.id]: e.target.value,
    });
  };

  const handleCheckbox = () => {
    setForm({
      ...form,
      updates: !form.updates,
    });
  };

  // ----------- SUBMIT FUNCTION -------------
  const handleRegister = async (e) => {
    e.preventDefault();

    if (!form.fullname || !form.email || !form.password || !form.mobile) {
      alert("Please fill all required fields.");
      return;
    }

    if (!form.work_status) {
      alert("Please select your work status.");
      return;
    }

    if (form.password.length < 8) {
      alert("Password must be at least 8 characters long.");
      return;
    }

    const hasUpper = /[A-Z]/.test(form.password);
    const hasNumber = /[0-9]/.test(form.password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(form.password);

    if (!hasUpper || !hasNumber || !hasSpecial) {
      alert("Password must include an uppercase letter, a number, and a special character.");
      return;
    }

    try {
      // Register via Express backend
      const res = await backendApi.auth.register({
        full_name: form.fullname,
        email: form.email,
        password: form.password,
        mobile: form.mobile,
        work_status: form.work_status,
        receive_updates: form.updates,
        role: "jobseeker",
      });

      login(res);
      
      // Redirect to Login page with OTP view active
      navigate("/jobs/auth", { 
        state: { 
          view: "otp-verify", 
          email: form.email,
          message: "Registration successful! Please verify your email with the OTP sent to " + form.email
        } 
      });
    } catch (error) {
      console.error("Registration failed:", error);
      alert(error.message || "Registration failed. Please try again.");
    }
  };

  return (
    <div className="w-full px-4 py-8 flex flex-col md:flex-row gap-8">
      {/* Left Info Card */}
      <div className="w-full md:w-[300px] bg-white border border-dashed border-gray-300 rounded-2xl p-8 sticky top-24">
        <h3 className="text-lg font-bold text-center text-gray-900 mb-6">
          On registering with <span className="text-[#2557a7]">webideal</span>, you can
        </h3>

        <ul className="space-y-4">
          {[
            "Build your profile and let recruiters find you",
            "Get job postings delivered right to your email",
            "Find a job and grow your career",
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-1 mt-0.5">
                <Check className="w-3 h-3 text-green-600 stroke-[4]" />
              </div>
              <span className="text-sm text-gray-600">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Form */}
      <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-1">
          Create your <span className="text-[#2557a7]">webideal</span> profile
        </h1>
        <p className="text-gray-500 text-sm mb-8">
          Search & apply to jobs from India’s No.1 career platform
        </p>

        <form onSubmit={handleRegister} className="space-y-6">
          {/* Full Name */}
          <div>
            <Label htmlFor="fullname">Full name *</Label>
            <Input
              id="fullname"
              value={form.fullname}
              onChange={handleChange}
              placeholder="Your name"
              className="h-11"
            />
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Your email"
              className="h-11"
            />
          </div>

          {/* Password */}
          <div>
            <Label htmlFor="password">Password *</Label>
            <Input
              id="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              placeholder="Minimum 8 characters (Strong)"
              className="h-11"
            />
          </div>

          {/* Mobile */}
          <div>
            <Label htmlFor="mobile">Mobile *</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">+91</span>
              <Input
                id="mobile"
                value={form.mobile}
                onChange={handleChange}
                placeholder="Mobile number"
                className="pl-14 h-11"
              />
            </div>
          </div>

          {/* Work Status */}
          <div className="pt-2">
            <Label>Work status *</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">

              {/* Experienced */}
              <div
                className={`border rounded-xl p-4 cursor-pointer relative ${form.work_status === "experienced"
                  ? "border-[#2557a7] bg-blue-50"
                  : "border-gray-200"
                  }`}
                onClick={() => setForm({ ...form, work_status: "experienced" })}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm">I'm experienced</h4>
                    <p className="text-xs text-gray-500">
                      I have work experience (excluding internships)
                    </p>
                  </div>
                  <Briefcase className="w-8 h-8 text-gray-400" />
                </div>

                {form.work_status === "experienced" && (
                  <Check className="absolute top-2 right-2 text-[#2557a7] w-4 h-4" />
                )}
              </div>

              {/* Fresher */}
              <div
                className={`border rounded-xl p-4 cursor-pointer relative ${form.work_status === "fresher"
                  ? "border-[#2557a7] bg-blue-50"
                  : "border-gray-200"
                  }`}
                onClick={() => setForm({ ...form, work_status: "fresher" })}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm">I'm a fresher</h4>
                    <p className="text-xs text-gray-500">I am a student / just graduated</p>
                  </div>
                  <GraduationCap className="w-8 h-8 text-gray-400" />
                </div>

                {form.work_status === "fresher" && (
                  <Check className="absolute top-2 right-2 text-[#2557a7] w-4 h-4" />
                )}
              </div>
            </div>
          </div>

          {/* Updates checkbox */}
          <div className="flex items-start gap-2">
            <Checkbox id="updates" checked={form.updates} onCheckedChange={handleCheckbox} />
            <Label htmlFor="updates" className="text-xs text-gray-500">
              Send me important updates & promotions via SMS, email, and WhatsApp
            </Label>
          </div>

          {/* Submit */}
          <Button type="submit" className="bg-[#2557a7] text-white rounded-full px-8 h-10">
            Register now
          </Button>
        </form>
      </div>
    </div>
  );
}
